import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import AccountValidateEmailModal, {
  AccountValidateEmailModalProps,
} from 'src/components/AccountValidateEmailModal'
import { useSagaTakeEvery } from 'src/hooks/useSaga'
import { useServiceRequest } from 'src/hooks/useServiceRequest'
import { actions, selectors } from 'src/redux'
import useUser from 'src/relay/User/useUser'
import * as Yup from 'yup'

const ValidateEmailModal: FC = () => {
  // Libaries

  const { t } = useTranslation()
  const [isOpen, setOpen] = useState(false)
  const $hasBeenClosed = useRef(false)

  // Selectors
  const { user } = useUser()

  // Request
  const [response, update] = useServiceRequest(
    selectors.auth.updateProfile,
    actions.auth.updateProfileRequest,
    actions.auth.updateProfileReset
  )

  useEffect(() => {
    if (user && user?.email.includes('@strapi.io') && !$hasBeenClosed.current) {
      setOpen(true)
    }
  }, [user])

  // Methods
  const closeModal = useCallback(() => {
    window.setTimeout(() => {
      setOpen(false)
      $hasBeenClosed.current = true
    }, 3000)
  }, [])

  const handleProfileUpdated = useCallback(() => {
    closeModal()
  }, [closeModal])

  useSagaTakeEvery(
    actions.auth.updateProfileSuccess,
    useCallback(handleProfileUpdated, [handleProfileUpdated])
  )

  const props: AccountValidateEmailModalProps = {
    isOpen,
    title: t('validate_email_modal_title'),
    form: {
      text: t('validate_email_modal_text'),
      validationSchema: Yup.object().shape({
        email: Yup.string().required(t('yup_required')).email(t('yup_email')),
      }),
      initialValues: {
        email: '',
      },
      fields: {
        email: {
          placeholder: t('validate_email_modal_email_placeholder'),
          label: t('validate_email_modal_email_label'),
          innerError: true,
          required: true,
        },
      },
      submitButton: {
        text: t('validate_email_modal_form_submit'),
        isLoading: response.pending,
      },
      onSubmit: (values) => {
        update({
          id: user?.id,
          email: values.email,
        })
      },
      ...(response.success && {
        success: t('validate_email_modal_form_success'),
      }),
      ...(response.errors && {
        errors: [{ label: t('validate_email_modal_form_error') }],
      }),
    },
  }

  return <AccountValidateEmailModal {...props} />
}

export default ValidateEmailModal
